import { FC, ReactNode } from 'react';
import { Avatar, Button, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar } from '@tabler/icons-react';
import { DemoTiming, isDemoBooked, ManagerCardState } from '@/PageComponents/Onboarding/onboarding-rules';
import { OnboardingManager } from '@/PageComponents/Onboarding/onboarding-config';
import InfoBadge from '@/PageComponents/Onboarding/components/InfoBadge';
import styles from '@/PageComponents/Onboarding/components/ManagerCard.module.css';

export interface ManagerCardProps {
    state: ManagerCardState;
    timing: DemoTiming;
    /** A demo has already taken place (per HubSpot). */
    demoHeld: boolean;
    demoManager: OnboardingManager;
    /** The account manager to present (see OnboardingDashboard); only shown in the managerAssigned state. */
    accountManager: OnboardingManager | null;
    /** When the upcoming demo starts (see formatDemoDate); null when there isn't one or HubSpot hasn't given a date yet. */
    demoDate?: string | null;
    onBookDemo: () => void;
    /** "What to expect": reopens the Before-you-start modal. */
    onWhatToExpect: () => void;
}

const HEADLINE = "Let's set up your business on ServCraft together.";

const ManagerCard: FC<ManagerCardProps> = ({ state, timing, demoHeld, demoManager, accountManager, demoDate = null, onBookDemo, onWhatToExpect }) => {
    // On phones "What to expect" sits beside the label instead of above the action.
    const isPhone = useMediaQuery('(max-width: 48em)');
    const assigned = state === 'managerAssigned' && accountManager ? accountManager : null;
    const manager = assigned ?? demoManager;
    const demoAhead = isDemoBooked(timing);
    // Only a demo still ahead has a date worth showing.
    const when = demoAhead ? demoDate : null;
    const bookedPill = <div className={styles.bookedPill} data-testid="onboarding-demo-booked"><IconCalendar size={20} />Demo booked</div>;

    const bookButton = (
        <Button id="onboarding-book-demo-manager-card" color="green" radius="xl" className={styles.action} leftSection={<IconCalendar size={20} />} onClick={onBookDemo}>
            Book a demo
        </Button>
    );

    let label: string;
    let body: string;
    let action: ReactNode = null;
    if (assigned) {
        label = `${assigned.name} · Your Account Manager`;
        if (demoAhead) {
            body = when ? `I will take you through features on the ${when}.` : 'During the demo I will go through features with you, and answer any questions.';
            action = bookedPill;
        } else if (demoHeld) {
            body = "I'm here to help you get the most out of ServCraft.";
        } else {
            body = 'Book a demo and I will go through features with you, and answer any questions.';
            action = bookButton;
        }
    } else if (state === 'noDemo') {
        label = `${demoManager.name} · Your Demo Manager`;
        body = "I'll set you up with an account manager to get setup 2 x faster.";
        action = bookButton;
    } else {
        label = `${demoManager.name} · Your Demo Manager`;
        if (demoAhead && when) {
            body = `Your demo is on the ${when}. Finding you an account manager...`;
            action = bookedPill;
        } else if (demoAhead) {
            // Booked, but HubSpot hasn't confirmed a date yet: the button shows it's in hand.
            body = 'Finding you an account manager and booking your demo...';
            action = (
                <Button color="green" radius="xl" className={`${styles.action} ${styles.pending}`} disabled
                    leftSection={<Loader size={18} color="white" type="oval" />}>
                    Booking your demo
                </Button>
            );
        } else {
            body = 'Finding you an account manager...';
        }
    }

    const whatToExpect = <InfoBadge id="onboarding-what-to-expect" label="What to expect" onClick={onWhatToExpect} className={styles.whatToExpect} />;

    const avatar = (
        <Avatar
            src={manager.photoUrl || null}
            alt={manager.name}
            size={isPhone ? 68 : 99}
            radius="50%"
            color="scBlue"
            className={styles.avatar}
            data-testid="onboarding-manager-avatar"
        >
            {manager.name.charAt(0).toUpperCase()}
        </Avatar>
    );
    const copy = (
        <>
            <Text className={styles.headline}>{HEADLINE}</Text>
            <Text className={styles.body} data-testid={when ? 'onboarding-demo-date' : undefined}>{body}</Text>
        </>
    );

    return (
        <Paper radius={4} className={styles.card} data-testid="onboarding-manager-card" data-state={state}>
            {isPhone ? (
                // Phones: the avatar beside who it is (and "What to expect"), everything else full width.
                <Stack gap={12} w="100%">
                    <Group gap={12} wrap="nowrap">
                        {avatar}
                        <Stack gap={6} align="flex-start">
                            <Text className={styles.label}>{label}</Text>
                            {whatToExpect}
                        </Stack>
                    </Group>
                    {copy}
                    {action}
                </Stack>
            ) : (
                <>
                    {avatar}
                    <Stack gap={8} className={styles.copy}>
                        <Text className={styles.label}>{label}</Text>
                        {copy}
                    </Stack>
                    <Stack gap={12} align="center" className={styles.side}>
                        {whatToExpect}
                        {action}
                    </Stack>
                </>
            )}
        </Paper>
    );
};

export default ManagerCard;
